import { z } from "zod";
import { SimProfileSchema } from "./scenarios.js";
import { SequenceCandidateResultSchema } from "./simulations-sequence.js";
import { ConfidenceDisplayShape } from "./track-record.js";
import {
  AnySimulationObjectiveSchema,
  ConfidenceLevelSchema,
  SimulationBatchKindSchema,
  SimulationBatchStatusSchema,
} from "./simulations-enums.js";

// Extraído de `simulations.ts` ao cruzar o teto de 200 linhas efetivas (guardrail #1)
// quando os campos de META entraram no candidato. Aqui vive o LOTE (candidatos, cold-start,
// resposta e item de lista); o arquivo original mantém enums, request e o resultado de uma rodada.

export const SimParamEnum = z.enum(["feedRate", "cycleTime", "mtbf", "mttr", "quality"]);

// Override aplicado pelo motor a uma máquina (com a influência que motivou a escolha).
// `baseProfile` é o perfil de cold-start (base medida/premissa) que o otimizador usou como ponto de
// partida — carregado junto do delta p/ que "Aplicar ao rascunho" reproduza o VALOR recomendado
// real (base × delta), não `default × delta`. Opcional: candidatos legados podem não tê-lo.
export const AutoCandidateChangeSchema = z.object({
  machineId: z.string().uuid(),
  machineName: z.string(),
  param: SimParamEnum,
  deltaPct: z.number(),
  influencePct: z.number(),
  assumed: z.boolean(),
  baseProfile: SimProfileSchema.optional(),
});

// Resultado POR MÁQUINA de um candidato. O motor sempre calculou isto; o lote devolvia
// só o agregado, e a tela não tinha como mostrar ONDE o ganho aconteceu (nem o assistente, como
// narrar). `availability` vem em PONTO PERCENTUAL, como os campos irmãos do candidato — o motor
// emite fração em `results.perMachine`, e a conversão mora na projeção, num lugar só.
export const CandidateMachineResultSchema = z.object({
  machineId: z.string(),
  name: z.string(),
  /** Vazão efetiva (capacidade × disponibilidade), un/h. */
  throughputUnH: z.number().finite().nullable(),
  /** Disponibilidade em pp (0–100). */
  availability: z.number().finite().nullable(),
  units: z.number().finite().nullable(),
  /** Gargalo DECLARADO pelo motor (`results.bottleneck`), nunca o mínimo recalculado na borda. */
  isBottleneck: z.boolean(),
});
export type CandidateMachineResult = z.infer<typeof CandidateMachineResultSchema>;

// Candidato deriva de uma Simulation filha + seus results.
export const AutoCandidateSchema = z.object({
  simulationId: z.string().uuid(),
  label: z.string().nullable(),
  isBaseline: z.boolean(),
  isWinner: z.boolean(),
  rankPosition: z.number().int().nullable(),
  status: z.enum(["DRAFT", "QUEUED", "RUNNING", "COMPLETED", "FAILED"]),
  /** O nível por PROCEDÊNCIA, que continua viajando ao lado e não muda de sentido (Spec 079/R2). */
  confidenceLevel: ConfidenceLevelSchema.nullable(),
  ...ConfidenceDisplayShape,
  oee: z.number().finite().nullable(),
  availability: z.number().finite().nullable(),
  performance: z.number().finite().nullable(),
  quality: z.number().finite().nullable(),
  estimatedProduction: z.number().finite().nullable(),
  bottleneckNames: z.array(z.string()),
  // Vazia em candidato que falhou e em lote antigo (aditivo, retrocompat).
  perMachine: z.array(CandidateMachineResultSchema).default([]),
  changes: z.array(AutoCandidateChangeSchema),
  // Presente só em candidatos de lote SEQUENCE (resultado por ordem da fila).
  sequence: SequenceCandidateResultSchema.nullable().default(null),
  // Meta de quantidade. Candidatos antigos não os têm; o registry cai na
  // extrapolação linear e a UI marca o número como estimativa.
  timeToTargetS: z.number().finite().nullable().default(null),
  targetReached: z.boolean().nullable().default(null),
  targetReachedRatio: z.number().finite().nullable().default(null),
  targetEstimated: z.boolean().nullable().default(null),
  // O instante da meta em RELÓGIO CIVIL, derivado das janelas congeladas do lote. O
  // `timeToTargetS` vive no eixo operacional (só horas de expediente): dizer "11,3 h" a quem opera
  // uma linha de 6 h/dia não responde "quando fica pronto", que é a pergunta. A conversão é do
  // servidor porque as janelas moram lá — mandar a matemática para o navegador convidaria as duas
  // pontas a divergir.
  targetReachedAt: z.string().datetime().nullable().default(null),
});
export type AutoCandidate = z.infer<typeof AutoCandidateSchema>;

// "product" = valor autorado por par (máquina×produto); acima de manual, abaixo de medido.
export const ColdStartTierSchema = z.enum(["measured", "similar", "product", "spec", "manual"]);
export type ColdStartTier = z.infer<typeof ColdStartTierSchema>;

export const ColdStartEntrySchema = z.object({
  machineId: z.string().uuid(),
  machineName: z.string(),
  tier: ColdStartTierSchema,
  assumed: z.boolean(),
});

export const AutoBatchResponseSchema = z.object({
  id: z.string().uuid(),
  lineId: z.string().uuid(),
  lineName: z.string(),
  // Discrimina qual conjunto de objetivos/candidatos o lote carrega.
  kind: SimulationBatchKindSchema,
  objective: AnySimulationObjectiveSchema,
  targetQuantity: z.number().int().nullable(),
  // Objetivo de prazo: de qual OP a promessa foi feita e qual o prazo, em
  // segundos relativos à âncora da execução (mesmo eixo de `timeToTargetS`, nunca relógio civil).
  productionOrderId: z.string().uuid().nullable().default(null),
  dueAtS: z.number().finite().nullable().default(null),
  horizonHours: z.number().int(),
  variationPct: z.number().int(),
  candidateCount: z.number().int(),
  status: SimulationBatchStatusSchema,
  partial: z.boolean(),
  evaluatedCount: z.number().int(),
  winnerSimulationId: z.string().uuid().nullable(),
  confidenceLevel: ConfidenceLevelSchema.nullable(),
  errorMessage: z.string().nullable(),
  baselineSimulationId: z.string().uuid().nullable(),
  candidates: z.array(AutoCandidateSchema),
  coldStart: z.array(ColdStartEntrySchema),
  createdAt: z.string(),
});
export type AutoBatchResponse = z.infer<typeof AutoBatchResponseSchema>;

export const AutoBatchListItemSchema = z.object({
  id: z.string().uuid(),
  lineName: z.string(),
  kind: SimulationBatchKindSchema,
  objective: AnySimulationObjectiveSchema,
  status: SimulationBatchStatusSchema,
  partial: z.boolean(),
  evaluatedCount: z.number().int(),
  candidateCount: z.number().int(),
  confidenceLevel: ConfidenceLevelSchema.nullable(),
  createdAt: z.string(),
});
export type AutoBatchListItem = z.infer<typeof AutoBatchListItemSchema>;
